import { logError } from '@zg-rentals/log-error';
import type { SyntheticEvent } from 'react';

/**
 * @file
 * Until all rentals-js apps are using the latest from page-frame-service
 * we have to manually attach the "cookie preference" handler.
 * This module exports two methods for handling this. The main way is `onPageFrameLinkClick`
 * which can be added as the `onLinkClick` prop to the "PageFooter" component from page-frame.
 * The other is a backup method that is currently invoked from our `defineAnalytics` function.
 * The back up is to catch cases of developers adding/modifying PageFooter and forgetting to add
 * the `onLinkClick` prop.
 */

const DATAGRAIL_ERROR_TYPE_PREFIX = 'DataGrailError';
const COOKIE_PREFERENCE_CLASS_NAME = 'cookie-preference';

function showConsentBanner(_event?: MouseEvent) {
  try {
    if (
      'DG_BANNER_API' in window &&
      typeof window.DG_BANNER_API === 'object' &&
      window.DG_BANNER_API !== null &&
      'showLayer' in window.DG_BANNER_API &&
      typeof window.DG_BANNER_API.showLayer === 'function'
    ) {
      // calling this more than once has no negative consequences
      window.DG_BANNER_API.showLayer('categories-layer');
    }
  } catch (error) {
    logError({
      error,
      errorType: `${DATAGRAIL_ERROR_TYPE_PREFIX}__ShowConsentBanner`,
      context: 'Failed to open Datagrail consent banner',
    });
  }
}

const waitForFooterElements = (callback: (elements: HTMLCollectionOf<HTMLElement>) => void): void => {
  let attempts = 0;
  // this is only to add the callback after the footer elements
  const maxAttempts = 10; // 10 seconds max (10 * 1 second)

  const poll = () => {
    // even though every element can have an `onclick` the base `Element` type does not support
    // that type, here we know the footer button/links will be HTML elements
    const elements = document.getElementsByClassName(COOKIE_PREFERENCE_CLASS_NAME) as HTMLCollectionOf<HTMLElement>;

    if (elements.length > 0) {
      callback(elements);
      return;
    }
    if (attempts >= maxAttempts) {
      logError({
        error: new Error('Failed to find footer elements to attach click handler'),
        errorType: `${DATAGRAIL_ERROR_TYPE_PREFIX}__WaitForFooterElements`,
      });
      return;
    }

    attempts++;
    setTimeout(poll, 1_000);
  };

  poll();
};

// it is recommended that all apps use the page frame service, not the components directly but...
// this functions more of a backup, in case a developer is using page frame components manually
// and forgets to attach an `onLinkClick`. However, this backup is not foolproof; if the footer
// is hidden before polling times out, the handler won't be attached
export function attachDatagrailClickHandler() {
  try {
    waitForFooterElements((elements) => {
      for (const el of elements) {
        // prefer `onclick` over `addEventListener` so there cannot be multiple callbacks executing
        el.onclick = showConsentBanner;
      }
    });
  } catch (error) {
    logError({
      error,
      errorType: `${DATAGRAIL_ERROR_TYPE_PREFIX}__AttachDatagrailClickHandler`,
    });
  }
}

// it is recommended that all apps use the page frame service, not the components directly but...
// for apps that aren't using page frame service, ie, using the PageFooter component directly
// they should use this for the `onLinkClick` prop
export function onPageFrameLinkClick(event: SyntheticEvent) {
  const target = event?.target;
  if (
    target != null &&
    'classList' in target &&
    target.classList !== null &&
    typeof target.classList === 'object' &&
    'value' in target.classList &&
    typeof target.classList.value === 'string' &&
    target.classList.value.includes(COOKIE_PREFERENCE_CLASS_NAME)
  ) {
    showConsentBanner();
  }
}
